import Grid from '@material-ui/core/Grid'
import { Formik, Form } from 'formik'
import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import useAssignProfileReviewMutation from '../../../apolloClient/mutations/assignProfileReview'
import { useOnboarding } from '../../../contexts/OnboardingContext'
import colors from '../../../shared/constants/colors'
import OnboardingSectionTitle from '../../../_global/OnboardingSectionTitle'
import ContactInfoRow from './ContactInfoRow'
import EducationRow from './EducationRow'
import ServiceLineRow from './ServiceLineRow'
import UploadImage from './UploadImage'
import WorkExperienceRow from './WorkExperienceRow'
import WorkPreferencesRow from './WorkPreferencesRow'
import YourPitchRow from './YourPitchRow'
import { useQuery } from '@apollo/react-hooks'
import EXPERT_QUERY from '../../../apolloClient/queries/getExpert'
import USER_INFO_QUERY from '../../../apolloClient/queries/getUserInfo'
import Error from '../../../_global/Error'
import Loader from '../../../_global/Loader'
import { Box } from '@material-ui/core'
import { useHistory } from 'react-router'
import * as Sentry from '@sentry/browser'
import FormActions from '../../../_global/FormActions'

const ROW_ITEMS = [
  { title: 'Contact Info', value: 'contactInfo' },
  { title: 'Primary Role', value: 'serviceLine' },
  { title: 'Education', value: 'education' },
  { title: 'Work Experience', value: 'experience' },
  { title: 'Work Preferences', value: 'preferences' },
  { title: 'Your Pitch', value: 'pitch' },
]

const getComponent = (item, data, currentExpert, setPropChange) => {
  const { value } = item
  const { getExpert } = data

  const props = { ...getExpert, ...currentExpert, setPropChange }

  switch (value) {
    case 'contactInfo':
      return <ContactInfoRow {...props} />
    case 'serviceLine':
      return <ServiceLineRow {...props} />
    case 'education':
      return <EducationRow {...props} />
    case 'experience':
      return <WorkExperienceRow {...props} />
    case 'preferences':
      return <WorkPreferencesRow {...props} />
    case 'pitch':
      return <YourPitchRow {...props} />
  }
}

const RowItem = styled(Grid)`
  border-top: solid 1px ${colors.borderGray};
`

const ProfileReview = () => {
  const { back, next, currentExpert, step } = useOnboarding()
  const [assignProfileReviewMutation] = useAssignProfileReviewMutation()
  const [propChange, setPropChange] = useState(false)
  const history = useHistory()

  const { loading, error, data, refetch } = useQuery(EXPERT_QUERY, {
    variables: { id: currentExpert.id },
    skip: !currentExpert.id,
  })

  const {
    loading: userLoading,
    error: userErrors,
    data: userData,
  } = useQuery(USER_INFO_QUERY, {
    variables: { id: currentExpert?.legacyFreelancerId },
    skip: !currentExpert,
  })

  useEffect(() => {
    if (propChange) {
      refetch({ id: currentExpert.id })
    }
  }, [refetch, propChange, currentExpert.id])

  if (loading || userLoading) return <Loader />
  if (error || userErrors) return <Error />

  const {
    getUserInfo: { email },
  } = userData

  const validForProfileReview =
    !!currentExpert &&
    !!currentExpert.legacyFreelancerId &&
    !!email &&
    !!currentExpert.legacyMetadata.primaryServiceLine

  const handleSubmit = async (values, actions) => {
    const assignProfileReviewInput = {
      freelancerId: values.legacyFreelancerId,
      serviceLine: values.legacyMetadata.primaryServiceLine,
      expertId: values.id,
      email,
    }

    try {
      const response = await assignProfileReviewMutation({ variables: { input: assignProfileReviewInput } })
      if (response.data.assignProfileReview) {
        await next()
        history.push('/registration/review')
      }
    } catch (error) {
      console.log('error')
      actions.setErrors({ message: 'Oops. Something went wrong!' })
      const context = { variables: JSON.stringify(assignProfileReviewInput), path: 'assignProfileReview' }
      Sentry.captureException(`Assign Profile Review Error: ${error}`, {
        contexts: { context },
      })
    }
  }

  return (
    <Formik initialValues={currentExpert} onSubmit={handleSubmit}>
      {({ isSubmitting, isValid, errors }) => {
        return (
          <Form autoComplete="off">
            <Grid container>
              <Grid item xs={12}>
                <OnboardingSectionTitle
                  title="Last Looks"
                  description="Take one last look at your profile, go back and edit any sections you’d like, and upload a photo."
                />
              </Grid>
              <RowItem item xs={12}>
                <Box py={2}>
                  <UploadImage userId={currentExpert.legacyFreelancerId} />
                </Box>
              </RowItem>
              {ROW_ITEMS.map((item, index) => {
                return (
                  <RowItem item xs={12} key={`${item.value}-${index}`}>
                    <Box my={3}>{getComponent(item, data, currentExpert, setPropChange)}</Box>
                  </RowItem>
                )
              })}
              {errors && <div>{errors.message}</div>}
            </Grid>
            <FormActions step={step} back={back} isValid={validForProfileReview} isSubmitting={isSubmitting} />
          </Form>
        )
      }}
    </Formik>
  )
}

export default ProfileReview
